import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ShopifyService } from '../../services/shopify.service';
import { CartService } from '../../services/cart.service';
import { ShopifyProduct } from '../models';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-shopify-products',
    templateUrl: './shopify-products.component.html',
    styleUrls: ['./shopify-products.component.scss'],
    imports: [FormsModule, RouterLink, TranslateModule],
})
export class ShopifyProductsComponent implements OnInit, OnDestroy {
  allProducts: ShopifyProduct[] = [];
  displayedProducts: ShopifyProduct[] = [];
  filteredProducts: ShopifyProduct[] = [];
  searchQuery: string = '';
  isLoading: boolean = true;
  loadError: boolean = false;
  showAddedToCartNotification: boolean = false;
  addedProductName: string = '';
  
  readonly INITIAL_DISPLAY_COUNT = 6;
  currentDisplayCount = this.INITIAL_DISPLAY_COUNT;

  private subscriptions: Subscription[] = [];
  private notificationTimeout: any;

  constructor(
    private shopifyService: ShopifyService,
    private cartService: CartService
  ) {}

  ngOnInit(): void {
    this.loadProducts();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
  }

  /**
   * Load all products from Shopify
   */
  private loadProducts(after?: string, accumulated: ShopifyProduct[] = []): void {
    this.isLoading = true;
    this.loadError = false;

    const sub = this.shopifyService.getProducts(250, undefined, after).subscribe({
      next: (response) => {
        if (response?.data?.products) {
          const products = this.parseProducts(response.data.products.edges);
          const allFetched = [...accumulated, ...products];

          const pageInfo = response.data.products.pageInfo;
          
          // If there are more products, fetch the next page
          if (pageInfo.hasNextPage && pageInfo.endCursor) {
            this.loadProducts(pageInfo.endCursor, allFetched);
          } else {
            // All products loaded - exclude Custom magnet, then sort showInitial first
            const filtered = allFetched.filter(p => !this.isCustomMagnetProduct(p));
            this.allProducts = this.sortByShowInitial(filtered);
            this.filteredProducts = [...this.allProducts];
            this.updateDisplayedProducts();
            this.isLoading = false;
          }
        } else {
          this.loadError = true;
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Error loading products:', error);
        this.loadError = true;
        this.isLoading = false;
      }
    });

    this.subscriptions.push(sub);
  }

  /** Exclude the Custom magnet product (used for make-yourself flow). */
  private isCustomMagnetProduct(product: ShopifyProduct): boolean {
    const title = (product.title || '').trim().toLowerCase();
    return title === 'custom magnet' || title === 'custom magnets' || title === 'gulli sticker';
  }

  /**
   * Parse GraphQL response into ShopifyProduct objects
   */
  private parseProducts(edges: any[]): ShopifyProduct[] {
    return edges
      .map(edge => edge.node)
      .filter(node => node.availableForSale && node.variants.edges.length > 0)
      .map(node => {
        const variant = node.variants.edges[0].node;
        const showInitial = node.metafield?.value === 'true';
        return {
          id: node.id,
          title: node.title,
          description: node.description || '',
          handle: node.handle,
          imageUrl: node.images.edges.length > 0 ? node.images.edges[0].node.url : '',
          price: {
            amount: variant.price.amount,
            currencyCode: variant.price.currencyCode
          },
          compareAtPrice: variant.compareAtPrice ? {
            amount: variant.compareAtPrice.amount,
            currencyCode: variant.compareAtPrice.currencyCode
          } : undefined,
          variantId: variant.id,
          availableForSale: variant.availableForSale,
          showInitial
        } as ShopifyProduct;
      });
  }

  /** Sort so products with showInitial come first. */
  private sortByShowInitial(products: ShopifyProduct[]): ShopifyProduct[] {
    return [...products].sort((a, b) => (b.showInitial ? 1 : 0) - (a.showInitial ? 1 : 0));
  }

  /**
   * Update displayed products based on current display count
   */
  private updateDisplayedProducts(): void {
    this.displayedProducts = this.filteredProducts.slice(0, this.currentDisplayCount);
  }

  /**
   * Handle search input
   */
  onSearchChange(): void {
    const query = this.searchQuery.toLowerCase().trim();
    
    if (query === '') {
      this.filteredProducts = [...this.allProducts];
    } else {
      this.filteredProducts = this.allProducts.filter(product =>
        product.title.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query)
      );
    }
    
    // Reset display count when searching
    this.currentDisplayCount = this.INITIAL_DISPLAY_COUNT;
    this.updateDisplayedProducts();
  }

  /**
   * Check if there are more products to display (show "See All" link)
   */
  get hasMore(): boolean {
    return this.displayedProducts.length < this.filteredProducts.length;
  }

  /**
   * Add product to cart
   */
  addToCart(product: ShopifyProduct): void {
    this.cartService.addShopifyProduct(product, 1);
    
    // Show notification
    this.addedProductName = product.title;
    this.showAddedToCartNotification = true;
    
    // Hide notification after 3 seconds
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
    this.notificationTimeout = setTimeout(() => {
      this.showAddedToCartNotification = false;
    }, 3000);
  }

  /**
   * Format price for display
   */
  formatPrice(amount: string, currencyCode: string): string {
    const price = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(price);
  }

  /**
   * Check if product has discount
   */
  hasDiscount(product: ShopifyProduct): boolean {
    if (!product.compareAtPrice) return false;
    return parseFloat(product.compareAtPrice.amount) > parseFloat(product.price.amount);
  }

  /**
   * Calculate discount percentage
   */
  getDiscountPercentage(product: ShopifyProduct): number {
    if (!product.compareAtPrice) return 0;
    const original = parseFloat(product.compareAtPrice.amount);
    const current = parseFloat(product.price.amount);
    return Math.round(((original - current) / original) * 100);
  }

  /**
   * Handle image load
   */
  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    const loader = img.parentElement?.querySelector('.image-loader');
    if (loader) {
      loader.classList.add('loaded');
    }
  }

  /**
   * Handle image error
   */
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const loader = img.parentElement?.querySelector('.image-loader');
    if (loader) {
      loader.classList.add('loaded');
    }
  }
}
